import React, { FC, useEffect } from 'react';
import { connect, useDispatch, useSelector } from 'react-redux';
import { compose } from 'react-recompose';
import { push as Push } from 'redux-first-history';
import { Overview } from '@dev/ee-modules';
import { withTranslation } from 'react-i18next';
import { AccessLevel, ProductCode } from '@dev/ee-api/ee-api-models';

import { ApplicationState as State } from '@/store';
import { ShowDialog } from '@/store/dialog/actions';
import { PurchasePrivateIP } from '@/store/account/actions';
import { AddPurchaseInfo, DeletePurchaseInfo } from '@/store/pools/actions';
import { PurchaseInfo } from '@/store/pools/types';
import { BillingPaths, LegacyPaths, SettingsPaths } from '@/services/dictionaries/Paths';
import { useUpselling } from '@/services/hooks/useUpselling';
import { useDashboardElementVisibility } from '@/services/hooks/useDashboardElementVisibility';
import { ShowToastParams } from '@/store/toast/types';
import { ResendActivationEmail } from '@/store/auth/actions';
import { ShowToast } from '@/store/toast/actions';
import { getProviders, LoadPaymentProviders } from '@/store/payments/actions';

import { SettingsOverviewProps as Props } from './OverviewInterface';

const mapState = (state: State, ownProps: Props): Props => ({
    ...ownProps,
    account: state.account,
    browser: state.browser,
    dialog: state.dialog,
    config: state.config,
    payments: state.payments,
});

const dispatchProps = {
    AddPurchaseInfo,
    DeletePurchaseInfo,
    Push,
    PurchasePrivateIP,
    ResendActivationEmail,
    ShowToast,
};
interface DispatchProps {
    AddPurchaseInfo(purchaseInfo: PurchaseInfo): void;
    DeletePurchaseInfo(): void;
    Push(url: string): void;
    PurchasePrivateIP(param: { region: string; quantity: number }): void;
    ResendActivationEmail?(params: { Email: string }): any;
    ShowToast?(params: ShowToastParams): void;
}

type ExtendedProps = Props & DispatchProps;

const Settings: FC<ExtendedProps> = (props: ExtendedProps): JSX.Element => {
    const { isUpsellingVisible } = useUpselling(AccessLevel.ModifyWebNotifications);
    const {
        config: { isStandalone, bootedAsChild },
    } = props;

    const dispatch = useDispatch();
    const providers = useSelector(getProviders);
    const { settings: {
        isSettingsAccessTokensVisible,
        isSettingsPanelVisible,
    }} = useDashboardElementVisibility();

    useEffect(() => {
        if (!providers) dispatch(LoadPaymentProviders());
    }, []);

    const goToBuyIp = () => {
        window.location.href = `${location.origin}${LegacyPaths.BuyLegacyIp}`;
    };

    const settingsProps = bootedAsChild ? { ...props, ...{ BuyIp: goToBuyIp } } : props;

    return (
        <Overview
            paths={{ ...SettingsPaths, ...BillingPaths }}
            marketingDashboard
            {...settingsProps}
            alert={!isStandalone}
            ShowDialog={(params) => {
                dispatch(ShowDialog(params));
            }}
            showUpselling={isUpsellingVisible}
            productCode={ProductCode.Marketing}
            isAccessTokensSettingsVisible={isSettingsAccessTokensVisible}
            isSettingsPanelVisible={isSettingsPanelVisible}
        />
    );
};

export default compose(withTranslation('settings'), connect<Props, DispatchProps>(mapState, dispatchProps))(Settings);
